import { Alert, Button, Card, DatePicker, Table, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import { placementKeys } from "./PlacementModal.tsx";

type RosterEntry = components["schemas"]["RosterEntry"];

/**
 * Students placed in the class on a chosen date (today by default). The roster is read from placement dates, so a past
 * date shows who was in the class then. Hidden for users without people.read.
 */
export function ClassRosterCard({ classId }: { classId: string }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canRead = hasPermission("people.read");
  const [date, setDate] = useState<Dayjs>(() => dayjs());
  const day = date.format("YYYY-MM-DD");

  const roster = useQuery({
    queryKey: placementKeys.roster(classId, day),
    enabled: canRead,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/roster", { params: { query: { ClassId: classId, Date: day } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  if (!canRead) return null;

  const columns = [
    {
      title: t("placement.student"),
      key: "name",
      ellipsis: true,
      render: (_: unknown, s: RosterEntry) => <Link to={`/students/${s.studentId}`}>{[s.lastName, s.firstName, s.middleName].filter(Boolean).join(" ")}</Link>,
    },
    {
      title: t("placement.status"),
      key: "status",
      width: 120,
      responsive: ["sm" as const],
      render: (_: unknown, s: RosterEntry) => <Tag color={s.status === "Active" ? "green" : "default"}>{t(`familyDetail.statuses.${s.status}`)}</Tag>,
    },
    {
      title: t("placement.inClass"),
      key: "period",
      width: 200,
      responsive: ["md" as const],
      render: (_: unknown, s: RosterEntry) => t("placement.period", { from: formatDate(s.from), to: s.to ? formatDate(s.to) : t("placement.ongoing") }),
    },
  ];

  return (
    <Card
      title={t("placement.roster")}
      extra={<DatePicker aria-label={t("placement.rosterDate")} value={date} format="DD.MM.YYYY" allowClear={false} onChange={(value) => setDate(value ?? dayjs())} />}
    >
      {roster.isError ? (
        <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void roster.refetch()}>{t("app.retry")}</Button>} />
      ) : (
        <>
          <Table<RosterEntry>
            rowKey="studentId"
            size="small"
            loading={roster.isPending}
            columns={columns}
            dataSource={roster.data ?? []}
            pagination={false}
            scroll={{ x: 360 }}
            locale={{ emptyText: t("placement.rosterEmpty") }}
          />
          {roster.data && roster.data.length > 0 && (
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              {t("placement.count", { count: roster.data.length })}
            </Typography.Text>
          )}
        </>
      )}
    </Card>
  );
}
